/**
 * Onboarding tour step definitions.
 *
 * Each step targets a `data-tour="<id>"` attribute in the DOM.
 * To update the tour, just edit this array — no component changes needed.
 */

export interface TourStep {
  /** Matches `data-tour="<id>"` on the target element */
  target: string;
  title: string;
  description: string;
  /** Preferred tooltip placement relative to the target */
  placement: 'top' | 'bottom' | 'left' | 'right';
}

export const DASHBOARD_TOUR_STEPS: TourStep[] = [
  {
    target: 'stat-grid',
    title: 'Dashboard snapshot',
    description:
      'The top row summarizes readiness, open findings, indexed components, and discovered tokens. Select a tile to drill into the work behind the number.',
    placement: 'bottom',
  },
  {
    target: 'setup-recommended',
    title: 'Start with the next best action',
    description:
      'This card chooses the most useful follow-up for the project, from open findings to governance setup or component review.',
    placement: 'bottom',
  },
  {
    target: 'setup-components',
    title: 'Review indexed components',
    description:
      'Start with the component inventory. Confirm primitives, compositions, and ownership so agents know what to reuse.',
    placement: 'bottom',
  },
  {
    target: 'setup-tokens',
    title: 'Enrich discovered tokens',
    description:
      'Map colors, spacing, typography, and intent so token guidance is clear when new UI is generated.',
    placement: 'bottom',
  },
  {
    target: 'setup-governance',
    title: 'Governance controls',
    description:
      'Connect CI to catch design-system issues before they ship, or manage the rules once governance is active.',
    placement: 'bottom',
  },
  {
    target: 'sidebar-nav',
    title: 'Explore your workspace',
    description:
      'Use the sidebar to move between the dashboard, pull requests, backlog, governance, and developer tools.',
    placement: 'right',
  },
];

export const DEMO_TOUR_STEPS: TourStep[] = [
  {
    target: 'stat-grid',
    title: 'See the value at a glance',
    description:
      'A weekly brief summarizes drift, open issues, and governance status — with a one-glance tile strip showing where an AI-built system is bleeding or improving.',
    placement: 'bottom',
  },
  {
    target: 'sidebar-nav',
    title: 'Tour the whole workspace',
    description:
      'Components, tokens, findings, and governance — everything you need to keep AI-generated UI aligned with your system.',
    placement: 'right',
  },
  {
    target: 'nav-findings',
    title: 'Findings catch drift early',
    description:
      'Every token violation, a11y gap, and unsafe prop shows up here — tied back to the exact file and line.',
    placement: 'right',
  },
  {
    target: 'try-cta',
    title: 'Try it with your repo',
    description:
      'Point Fragments at a real repo and you will see your components, tokens, and drift — scanned in under a minute.',
    placement: 'bottom',
  },
];

export const WAITLIST_TOUR_STEPS: TourStep[] = [
  {
    target: 'stat-grid',
    title: 'Your UI governance layer',
    description:
      'Fragments tracks readiness, open findings, component coverage, and token discovery in one compact dashboard snapshot.',
    placement: 'bottom',
  },
  {
    target: 'setup-recommended',
    title: 'Know what to do next',
    description:
      'Fragments turns the dashboard signals into a recommended next action, so teams can move from overview to cleanup quickly.',
    placement: 'bottom',
  },
  {
    target: 'setup-components',
    title: 'Understand your component system',
    description:
      'Review indexed components, confirm primitives and compositions, and give agents a reliable map of what already exists.',
    placement: 'bottom',
  },
  {
    target: 'setup-tokens',
    title: 'Turn tokens into guidance',
    description:
      'Discovered tokens become structured color, spacing, typography, and intent guidance that generated UI can follow.',
    placement: 'bottom',
  },
  {
    target: 'setup-governance',
    title: 'Catch issues before they spread',
    description:
      'Connect governance to CI so hardcoded values, token drift, accessibility gaps, and unsafe patterns are caught before release.',
    placement: 'bottom',
  },
  {
    target: 'sidebar-nav',
    title: 'Built for the full workflow',
    description:
      'The full platform extends beyond the dashboard into components, tokens, findings, governance, and developer setup.',
    placement: 'right',
  },
];

/** localStorage key for tracking tour completion */
export const TOUR_STORAGE_KEY = 'fragments-cloud:tour:dashboard';

// Kept separate from the authed Dashboard key so a signed-in user who visits
// /demo still gets the demo tour, and vice versa.
export const DEMO_TOUR_STORAGE_KEY = 'fragments-cloud:tour:demo';

// Sidebar footer "Replay tour" lives outside the TourProvider subtree; it
// dispatches this event so the provider can restart without a route remount.
export const DEMO_TOUR_RESTART_EVENT = 'fragments:demo-tour:restart';
